import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import { ADMIN_WORKFLOW_KEYS } from "./adminWorkflowDefinitions.mjs";
import {
  createAdminWorkflowInstance,
  fetchAdminWorkflowInstances,
  fetchMailjetWorkflowOptions,
  runAdminWorkflowInstance,
  updateAdminWorkflowInstance,
} from "./adminWorkflowsApi.mjs";

const defaultForm = () => ({
  id: null,
  name: "Mailjet event mirror",
  status: "paused",
  sourceCompanyId: null,
  sourceAccountId: null,
  targetCompanyId: null,
  targetAccountId: null,
  scheduleIntervalMinutes: 60,
});

export default {
  data() {
    return {
      loading: false,
      saving: false,
      running: false,
      errorMessage: "",
      successMessage: "",
      workflows: [],
      companies: [],
      selectedWorkflowId: null,
      form: defaultForm(),
    };
  },
  computed: {
    mailjetWorkflows() {
      return this.workflows.filter((workflow) =>
        (workflow.workflowKey || workflow.workflow_key) === ADMIN_WORKFLOW_KEYS.MAILJET_EVENT_MIRROR
      );
    },
    selectedWorkflow() {
      return this.mailjetWorkflows.find((workflow) => String(workflow.id) === String(this.selectedWorkflowId)) || null;
    },
    mailjetMirroredCount() {
      return this.mailjetWorkflows.reduce((total, workflow) => {
        return total + Number(workflow.mirroredEventsCount || workflow.mirrored_events_count || 0);
      }, 0);
    },
    activeWorkflowCount() {
      return this.mailjetWorkflows.filter((workflow) => workflow.status === "active").length;
    },
    companyItems() {
      return this.companies.map((company) => ({ id: Number(company.id), name: company.name }));
    },
    sourceAccountItems() {
      return this.accountItemsForCompany(this.form.sourceCompanyId);
    },
    targetAccountItems() {
      return this.accountItemsForCompany(this.form.targetCompanyId);
    },
    frequencyItems() {
      return [null, 15, 30, 60, 360, 720, 1440].map((value) => ({
        value, label: this.$t(`admin.workflows.frequencies.${value || "manual"}`),
      }));
    },
    statusItems() {
      return ["paused", "active", "draft"].map((value) => ({ value, label: this.statusLabel(value) }));
    },
    kpiItems() {
      return [
        { key: "configured", label: this.$t("admin.workflows.metrics.configured"), value: this.mailjetWorkflows.length, icon: "mdi-cog-outline", color: "primary" },
        { key: "active", label: this.$t("admin.workflows.metrics.active"), value: this.activeWorkflowCount, icon: "mdi-play-circle-outline", color: "success" },
        { key: "mirrored", label: this.$t("admin.workflows.metrics.mailjetEvents"), value: this.mailjetMirroredCount, icon: "mdi-email-check-outline", color: "info" },
        { key: "admin", label: this.$t("admin.workflows.metrics.access"), value: this.$t("admin.workflows.metrics.adminOnly"), icon: "mdi-shield-crown-outline", color: "secondary" },
      ];
    },
    headers() {
      return [
        { title: this.$t("admin.workflows.table.name"), key: "name", sortable: false },
        { title: this.$t("admin.workflows.table.status"), key: "status", sortable: false },
        { title: this.$t("admin.workflows.table.frequency"), key: "frequency", sortable: false },
        { title: this.$t("admin.workflows.table.mirrored"), key: "mirrored", sortable: false },
        { title: this.$t("admin.workflows.table.lastRun"), key: "lastRun", sortable: false },
        { title: "", key: "action", sortable: false, width: 48 },
      ];
    },
    workflowRows() {
      return this.mailjetWorkflows.map((workflow) => ({
        ...workflow,
        raw: workflow,
        frequency: this.frequencyLabel(workflow.scheduleIntervalMinutes || workflow.schedule_interval_minutes),
        mirrored: workflow.mirroredEventsCount || workflow.mirrored_events_count || 0,
        lastRun: this.formatDate(workflow.lastRunAt || workflow.last_run_at),
      }));
    },
    canSave() {
      return Boolean(this.form.name && this.form.sourceCompanyId && this.form.sourceAccountId &&
        this.form.targetCompanyId && this.form.targetAccountId && this.form.sourceAccountId !== this.form.targetAccountId);
    },
    canRun() {
      return Boolean(this.form.id && this.canSave);
    },
  },
  watch: {
    "form.sourceCompanyId"() {
      if (!this.sourceAccountItems.some((account) => account.id === this.form.sourceAccountId)) this.form.sourceAccountId = null;
    },
    "form.targetCompanyId"() {
      if (!this.targetAccountItems.some((account) => account.id === this.form.targetAccountId)) this.form.targetAccountId = null;
    },
  },
  mounted() {
    this.loadWorkspace();
  },
  methods: {
    async loadWorkspace() {
      this.loading = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const [instancesResponse, optionsResponse] = await Promise.all([
          fetchAdminWorkflowInstances(),
          fetchMailjetWorkflowOptions(),
        ]);
        this.workflows = Array.isArray(instancesResponse.data?.workflows) ? instancesResponse.data.workflows : [];
        this.companies = Array.isArray(optionsResponse.data?.companies) ? optionsResponse.data.companies : [];
        const selected = this.selectedWorkflow || this.mailjetWorkflows[0] || null;
        if (selected) this.selectWorkflow(selected);
        else this.newMirror();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.workflows.errors.load");
      } finally {
        this.loading = false;
      }
    },
    newMirror() {
      this.selectedWorkflowId = null;
      this.form = defaultForm();
    },
    selectWorkflow(workflow) {
      if (!workflow) return;
      const settings = workflow.settings || workflow.settings_json || {};
      this.selectedWorkflowId = workflow.id;
      this.form = {
        id: workflow.id,
        name: workflow.name || "Mailjet event mirror",
        status: workflow.status || "paused",
        sourceCompanyId: Number(settings.sourceCompanyId) || null,
        sourceAccountId: Number(settings.sourceAccountId) || null,
        targetCompanyId: Number(settings.targetCompanyId) || null,
        targetAccountId: Number(settings.targetAccountId) || null,
        scheduleIntervalMinutes: workflow.scheduleIntervalMinutes ?? workflow.schedule_interval_minutes ?? null,
      };
    },
    accountItemsForCompany(companyId) {
      const company = this.companies.find((entry) => Number(entry.id) === Number(companyId));
      return (company?.accounts || []).map((account) => ({ id: Number(account.id), label: this.accountLabel(account) }));
    },
    accountLabel(account) {
      const status = account.lastTestStatus || account.last_test_status || "unknown";
      const email = account.mailjetAccountEmail || account.mailjet_account_email || "";
      return email ? `${account.name} - ${email} - ${status}` : `${account.name} - ${status}`;
    },
    payloadFromForm() {
      return {
        workflowKey: ADMIN_WORKFLOW_KEYS.MAILJET_EVENT_MIRROR,
        name: this.form.name,
        status: this.form.status,
        scheduleIntervalMinutes: this.form.scheduleIntervalMinutes,
        settings: {
          sourceCompanyId: this.form.sourceCompanyId,
          sourceAccountId: this.form.sourceAccountId,
          targetCompanyId: this.form.targetCompanyId,
          targetAccountId: this.form.targetAccountId,
        },
      };
    },
    async saveWorkflow() {
      if (!this.canSave) return;
      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const payload = this.payloadFromForm();
        const response = this.form.id
          ? await updateAdminWorkflowInstance(this.form.id, payload)
          : await createAdminWorkflowInstance(payload);
        const workflow = response.data?.workflow;
        if (workflow) {
          this.upsertWorkflow(workflow);
          this.selectWorkflow(workflow);
        }
        const warnings = Array.isArray(response.data?.warnings) ? response.data.warnings : [];
        this.successMessage = warnings.length
          ? `${this.$t("admin.workflows.messages.saved")} ${warnings.join(" ")}`
          : this.$t("admin.workflows.messages.saved");
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.workflows.errors.save");
      } finally {
        this.saving = false;
      }
    },
    async runWorkflow() {
      if (!this.canRun) return;
      this.running = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const { data } = await runAdminWorkflowInstance(this.form.id);
        const copied = data?.summary?.copied ?? data?.run?.itemsProcessed ?? 0;
        this.successMessage = this.$t("admin.workflows.messages.runComplete", { copied });
        await this.loadWorkspace();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.workflows.errors.run");
      } finally {
        this.running = false;
      }
    },
    upsertWorkflow(workflow) {
      const index = this.workflows.findIndex((entry) => entry.id === workflow.id);
      if (index >= 0) this.workflows.splice(index, 1, workflow);
      else this.workflows.unshift(workflow);
    },
    workflowRowProps({ item }) {
      const raw = item?.raw || item;
      return {
        class: raw?.id === this.selectedWorkflowId ? "admin-mailjet-mirror__row admin-mailjet-mirror__row--active" : "admin-mailjet-mirror__row",
        style: "cursor: pointer;",
        tabindex: 0,
      };
    },
    handleWorkflowClick(_, row) {
      this.selectWorkflow(row?.item?.raw || row?.item);
    },
    frequencyLabel(value) {
      const numeric = value === null || value === undefined ? null : Number(value);
      return this.frequencyItems.find((entry) => entry.value === numeric)?.label || this.$t("admin.workflows.frequencies.manual");
    },
    statusLabel(status) { return this.$t(`admin.workflows.statuses.${status || "draft"}`); },
    statusColor(status) { return status === "active" ? "success" : status === "paused" ? "warning" : "secondary"; },
    formatDate(value) {
      const locale = this.$i18n?.locale === "en" ? "en-US" : this.$i18n?.locale === "ru" ? "ru-RU" : "ro-RO";
      return formatExternalApiDateTime(value, locale);
    },
  },
};
